/**
 * @file src/room/roomLeases.js
 * @description The realm's one answer to "does someone else have a hand on this control right
 * now?" — the room client folds the relay's stream into it, every control reads out of it.
 *
 * The arbitration itself is a pure fold and lives in `paramLease.js`. Three things have to be
 * added around it before a UI can use it, and each is the kind of thing that goes wrong quietly
 * when every caller does its own version:
 *
 * **Reading the relay's clock without owning one.** Holds are measured in relay time (`rt`), which
 * this tab has no direct access to. It is estimated as an OFFSET from the local monotonic clock,
 * keeping the LARGEST offset any message has implied. The largest comes from the least-delayed
 * message, so it is also the most accurate — and because it only ever grows, the estimate can
 * never run backwards and make an open hold look younger than it did a moment ago. A message that
 * sat on the wire simply contributes nothing.
 *
 * **Keying by the deck, not by what this tab has loaded.** A hold is stored under the WIRE voice
 * id, the positional deck index the address itself names. The runtime voice behind that deck
 * materializes asynchronously and at different moments on different machines, so keying on it
 * would mean a client that was still loading silently skipped events its peers folded — and two
 * clients folding different streams is precisely the divergence this exists to prevent. The
 * translation happens on the way OUT, when a mounted control asks about itself.
 *
 * **Telling the UI when nothing happened.** A hold ends because time passed, not because a
 * message arrived, so a control drawn as held has nothing to re-render it. Every entry point
 * re-derives who holds what and announces only when that answer changed, and a single tick is
 * armed at the earliest expiry so the answer is re-read even in silence. The tick decides nothing:
 * the verdict for a given instant is the same whether it fired on time, late, or never.
 */
import { createLeaseLedger, leaseKey, HOLD_MS } from './paramLease.js';

/**
 * A local clock that only goes forward. The wall clock does not qualify — a time sync stepping it
 * back would age every open hold backwards, and a step forward would expire them early — and this
 * measures nothing but ELAPSED time, which is what `performance.now()` is for.
 */
/** What `#signature` returns for an idle ledger — the value the fast path compares against. */
const EMPTY_SIGNATURE = '\n';

function monotonicNow() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

export class RoomLeases {
  #ledger;
  #listeners = new Set();
  #now;
  #setTimer;
  #clearTimer;
  /** deck lookup, installed by the room client; outside a room nothing resolves and nothing holds. */
  #resolveWireId = () => null;
  /** Largest `rt − localNow` any message has implied; null until the first stamped message. */
  #offset = null;
  #tick = null;
  /** The last answer announced, so a repeat is not announced again. */
  #announced = '\n';
  /** Identity of the current lookup owner, so a late teardown cannot unbind a newer one. */
  #lookupToken = null;
  /**
   * Relay time until which this ledger refuses to grant anything, or null when it vouches for
   * itself. Set after a REJOIN — see `reset`.
   */
  #warmUntilRt = null;
  #warmPending = false;

  constructor({ now = monotonicNow, setTimer, clearTimer, resolveWireId } = {}) {
    this.#now = now;
    const canSchedule = typeof setTimeout === 'function';
    this.#setTimer = setTimer ?? (canSchedule ? ((fn, ms) => setTimeout(fn, ms)) : () => null);
    this.#clearTimer = clearTimer ?? (canSchedule ? ((id) => clearTimeout(id)) : () => {});
    if (resolveWireId) this.#resolveWireId = resolveWireId;
    this.#ledger = createLeaseLedger();
  }

  /**
   * Install the deck lookup — runtime voice id → the positional wire id the addresses use.
   *
   * The room client already owns that translation and is the only thing that knows the roster, so
   * it hands it over rather than this reaching for a store of its own. Two places deciding which
   * deck a voice is would be two answers eventually.
   */
  bindVoiceLookup(resolveWireId) {
    const token = {};
    this.#lookupToken = token;
    this.#resolveWireId = typeof resolveWireId === 'function' ? resolveWireId : () => null;
    this.#announce();
    // Unbinding is scoped to THIS binding. A room client torn down after its replacement has
    // already bound would otherwise blank the live room's lookup, and every control in it would
    // read "free" while the relay went on refusing the writes.
    return () => {
      if (this.#lookupToken !== token) return;
      this.#lookupToken = null;
      this.#resolveWireId = () => null;
      this.#announce();
    };
  }

  /**
   * The deck→voice mapping changed (a deck materialized, the roster moved).
   *
   * Holds are keyed by deck, so the ledger itself is unchanged — but the ANSWER a mounted control
   * gets did change, and nothing else would say so. Without this a control that mounted while its
   * deck was still loading stays drawn as free over a control the write gate refuses: a knob that
   * looks available and does nothing, which is worse than one that looks locked.
   */
  voiceLookupChanged() {
    this.#announce();
  }

  /**
   * Note the relay clock carried by any stamped message — not only a parameter write. Time passing
   * is what ends a hold, and this tab learns that time from whatever the room happens to be doing.
   */
  noteRelayClock(rt) {
    if (this.#takeStamp(rt)) this.#reconcile();
  }

  /**
   * Bookkeeping for one relay stamp, without announcing — so a fold reconciles ONCE rather than
   * twice (its clock note and then its own outcome), which would report a change that nothing
   * observed in between.
   * @returns {boolean} whether the stamp was usable.
   */
  #takeStamp(rt) {
    if (!Number.isFinite(rt)) return false;
    const implied = rt - this.#now();
    if (this.#offset === null || implied > this.#offset) this.#offset = implied;
    if (this.#warmPending) {
      this.#warmPending = false;
      this.#warmUntilRt = rt + HOLD_MS;
    }
    return true;
  }

  /** Relay time, now — or null when no stamped message has arrived yet. */
  relayNow() {
    return this.#offset === null ? null : this.#now() + this.#offset;
  }

  /**
   * Fold one relayed parameter write, keyed by the WIRE voice id the address names.
   * @returns {boolean} whether the write is the holder's, and so should be applied.
   */
  fold({ wireVoiceId, paramName, dimensionId, clientId, intent, rt }) {
    if (wireVoiceId == null || !paramName) return true;
    if (!clientId || !this.#takeStamp(rt)) return true; // nothing to arbitrate with
    const allowed = this.#ledger.apply({
      key: leaseKey(wireVoiceId, paramName, dimensionId),
      clientId,
      intent,
      rt,
    });
    this.#reconcile();
    return allowed;
  }

  /**
   * Fold one relayed GENERATOR write — an oscillator's, a tilt sensor's — keyed like
   * {@link fold} but touching no hold: a generator is not a hand, so its stream must neither open
   * a hold nor end one (a `commit` folded the ordinary way would close its own sender's live
   * gesture on the same control). It still notes the relay clock and still yields to a hand —
   * while someone else's hold is open, every client refuses the stream the same way.
   * @returns {boolean} whether the write may apply.
   */
  foldGenerator({ wireVoiceId, paramName, dimensionId, clientId, rt }) {
    if (wireVoiceId == null || !paramName) return true;
    if (!clientId || !this.#takeStamp(rt)) return true; // nothing to arbitrate with
    this.#reconcile();
    return this.#ledger.canWrite(leaseKey(wireVoiceId, paramName, dimensionId), clientId, rt);
  }

  /** Who has a hand on this control, or null when it is free (or we cannot tell the time yet). */
  holderOf(voiceId, paramName, dimensionId) {
    const rt = this.relayNow();
    const wireVoiceId = this.#resolveWireId(voiceId);
    if (rt === null || wireVoiceId === null) return null;
    return this.#ledger.holderOf(leaseKey(wireVoiceId, paramName, dimensionId), rt);
  }

  /**
   * Is this ledger still catching up on a stream it missed?
   *
   * A fold is only as good as the stream behind it, and an EMPTY one is not the same as a stream
   * that says nothing is held — it is a ledger with no opinion. Left to answer anyway it would
   * hand its own user a control someone else is visibly driving, and because both hands then keep
   * extending their own holds, the two sides never come back together.
   *
   * One hold window of listening settles it: any hold still open must have had a live message
   * inside that window, so once the window has passed this ledger has seen everything that could
   * still matter and its silence means what it says.
   */
  warming() {
    if (this.#warmPending) return true;
    if (this.#warmUntilRt === null) return false;
    const rt = this.relayNow();
    return rt === null || rt < this.#warmUntilRt;
  }

  /** Is this control held by someone OTHER than `clientId`? The question a control asks about itself. */
  heldByOther(voiceId, paramName, dimensionId, clientId) {
    const holder = this.holderOf(voiceId, paramName, dimensionId);
    return holder !== null && holder !== clientId;
  }

  /** May `clientId` write this control right now? True when free, or already theirs. */
  canWrite(voiceId, paramName, dimensionId, clientId) {
    if (this.warming()) return false;
    return !this.heldByOther(voiceId, paramName, dimensionId, clientId);
  }

  /** A member left: they cannot let go of anything, so drop what they were holding. */
  forget(clientId) {
    this.#ledger.forget(clientId);
    this.#reconcile();
  }

  /**
   * Drop every hold and the clock estimate.
   *
   * Called on teardown and on every (re)join. A rejoin is the one moment this tab KNOWS it may
   * have missed part of the stream, and a fold is only as good as the stream behind it. Starting
   * empty costs nothing — a gesture still in progress re-opens its hold on its next live message,
   * which is at most a thinning interval away — and it means no hold can outlive the stream that
   * would have expired it.
   */
  reset({ warm = false } = {}) {
    this.#ledger.clear();
    this.#offset = null;
    this.#cancelTick();
    // `warm` says this tab may have missed part of the stream — a REJOIN, never a first join,
    // where there is no earlier stream to have missed. Until a full hold window of it has been
    // heard, this ledger grants nothing (see `warming`).
    this.#warmPending = warm;
    this.#warmUntilRt = null;
    // Seeded with what an empty ledger says, so the first stamped message is not reported as a
    // change to an answer nobody had yet asked for.
    this.#announced = this.#signature(null);
    this.#announce();
  }

  /** Subscribe to "who holds what" changing. Returns an unsubscribe fn. */
  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  /**
   * Re-derive the current answer; announce it if it changed, and re-arm the look-again tick.
   *
   * Every entry point runs this, including the plain clock note — a hold that ran out while the
   * room was busy with something else has genuinely changed the answer, and nothing else would
   * ever say so. Live writes arrive many times a second and almost all of them leave the answer
   * exactly as it was, so the comparison is what keeps a drag from re-rendering every control on
   * every step.
   */
  #reconcile() {
    const rt = this.relayNow();
    if (rt === null) return;
    // Nothing held and nothing was: the overwhelmingly common case, and the one that runs on every
    // single message the room sends. Answer it with one integer compare rather than rebuilding a
    // description of an empty ledger a hundred times a second.
    if (this.#ledger.size === 0 && this.#announced === EMPTY_SIGNATURE) return;
    const signature = this.#signature(rt);
    if (signature !== this.#announced) {
      this.#announced = signature;
      this.#announce();
    }
    this.#armTick(rt);
  }

  /** The whole current answer as one comparable string: are we warming, and who holds what. */
  #signature(rt) {
    const held = rt === null ? '' : this.#ledger.openHolders(rt);
    return `${this.warming() ? 'warming' : ''}\n${held}`;
  }

  /**
   * Make sure ONE look-again tick is pending. Not rescheduled when one already is: a drag extends
   * its hold thirty times a second, and cancelling and re-arming a timer on each of those is pure
   * churn for an answer nothing is waiting on. A tick that fires early simply finds the hold still
   * open and arms the next one — harmless, because the verdict for an instant is computed on read
   * and never depends on when the tick ran.
   */
  #armTick(rt) {
    if (this.#tick !== null) return;
    // The end of the warm-up changes the answers too, and no message need arrive to cause it.
    const holdExpiry = this.#ledger.nextExpiryRt(rt);
    const warmExpiry = this.warming() ? this.#warmUntilRt : null;
    let expiry = holdExpiry;
    if (Number.isFinite(warmExpiry) && (expiry === null || warmExpiry < expiry)) expiry = warmExpiry;
    if (expiry === null) return;
    this.#tick = this.#setTimer(() => {
      this.#tick = null;
      this.#reconcile(); // re-arms behind this one for any hold still running
    }, Math.max(0, expiry - rt));
  }

  #cancelTick() {
    if (this.#tick !== null) {
      this.#clearTimer(this.#tick);
      this.#tick = null;
    }
  }

  #announce() {
    for (const fn of this.#listeners) {
      try {
        fn();
      } catch (error) {
        console.warn('[room] lease listener failed', error);
      }
    }
  }
}

/** The realm's one lease seam (empty outside room mode — every control then reads "free"). */
export const roomLeases = new RoomLeases();
